// src/components/LocationPicker/LocationPicker.js
import React, { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet'
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress
} from '@mui/material'
import { MyLocation, CheckCircle } from '@mui/icons-material'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix para ícones do Leaflet no React
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Componente para atualizar o centro do mapa dinamicamente
function MapCenterUpdater({ center }) {
  const map = useMap()

  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom())
    }
  }, [center, map])

  return null
}

// Componente interno para capturar cliques no mapa
function LocationMarker({ position, onPositionChange }) {
  useMapEvents({
    click(e) {
      onPositionChange([e.latlng.lat, e.latlng.lng])
    }
  })

  return position ? <Marker position={position} /> : null
}

const LocationPicker = ({
  latitude,
  longitude,
  street,
  city,
  state,
  onLocationChange
}) => {
  const [position, setPosition] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mapCenter, setMapCenter] = useState([-23.5505, -46.6333]) // São Paulo como padrão
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    // Se já tem latitude e longitude, usar como posição inicial
    if (latitude && longitude) {
      const coords = [parseFloat(latitude), parseFloat(longitude)]
      setPosition(coords)
      setMapCenter(coords)
      setLoading(false)
      setHasInitialized(true)
      return
    }

    // Se tem endereço mas ainda não inicializou, buscar coordenadas
    if (street && city && state && !hasInitialized) {
      geocodeAddress()
    } else if (!hasInitialized) {
      setLoading(false)
      setHasInitialized(true)
    }
  }, [latitude, longitude, street, city, state, hasInitialized])

  const geocodeAddress = async () => {
    try {
      const address = `${street}, ${city}, ${state}, Brasil`
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`
      )
      const data = await response.json()

      if (data && data.length > 0) {
        const coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)]
        setPosition(coords)
        setMapCenter(coords)
        setHasInitialized(true)
      }
    } catch (error) {
      console.error('Erro ao geocodificar endereço:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePositionChange = (newPosition) => {
    setPosition(newPosition)
    if (onLocationChange) {
      onLocationChange({
        latitude: newPosition[0],
        longitude: newPosition[1]
      })
    }
  }

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      setLoading(true)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = [pos.coords.latitude, pos.coords.longitude]
          setPosition(coords)
          setMapCenter(coords)
          if (onLocationChange) {
            onLocationChange({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            })
          }
          setLoading(false)
        },
        (error) => {
          console.error('Erro ao obter localização:', error)
          setLoading(false)
        }
      )
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 2
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.300' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}
        >
          <Typography variant='subtitle1' fontWeight='bold'>
            📍 Selecione a localização no mapa
          </Typography>
          <Button
            size='small'
            startIcon={<MyLocation />}
            onClick={handleUseMyLocation}
            variant='outlined'
          >
            Usar minha localização
          </Button>
        </Box>
        <Typography variant='caption' color='text.secondary'>
          Clique no mapa para marcar a localização exata do imóvel
        </Typography>
      </Box>

      {position && (
        <Alert
          severity='success'
          icon={<CheckCircle />}
          sx={{
            m: 2,
            mb: 0,
            borderRadius: 1
          }}
        >
          Localização selecionada: {position[0].toFixed(6)}, {position[1].toFixed(6)}
        </Alert>
      )}

      <Box
        sx={{
          height: 400,
          position: 'relative',
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            cursor: 'crosshair'
          }
        }}
      >
        <MapContainer
          center={mapCenter}
          zoom={position ? 16 : 13}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <MapCenterUpdater center={mapCenter} />
          <LocationMarker
            position={position}
            onPositionChange={handlePositionChange}
          />
        </MapContainer>
      </Box>

      {!position && (
        <Box sx={{ p: 2, bgcolor: 'warning.50', borderTop: '1px solid', borderColor: 'warning.200' }}>
          <Typography variant='caption' color='warning.dark'>
            ⚠️ Nenhuma localização selecionada. Clique no mapa para marcar.
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default LocationPicker
